import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MainLayouts from "../layout/MainLayouts";
import contactHeroBg from "../assets/images/young-student-working-assignment.webp";

const INFO_ICONS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.01-1.03.387-1.31l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    ),
    value: "+1 (202) 933-9591",
    href: "tel:+12029339591",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    value: "info@e1xacademy.net",
    href: "mailto:info@e1xacademy.net",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

function ContactPage() {
  const { t } = useTranslation();
  const INFO_ITEMS = [
    { ...INFO_ICONS[0], label: t("contact.info.officeAddress"), value: t("contact.info.officeAddressValue") },
    { ...INFO_ICONS[1], label: t("contact.info.phone") },
    { ...INFO_ICONS[2], label: t("contact.info.email") },
    { ...INFO_ICONS[3], label: t("contact.info.workingHours"), value: t("contact.info.workingHoursValue") },
  ];
  const [form, setForm] = useState({
    firstname: "", lastname: "", email: "", phone: "", subject: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <MainLayouts>
      {/* Hero */}
      <div className="relative pt-32 pb-24 lg:pt-44 lg:pb-32 bg-[#0b0f19] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={contactHeroBg}
            alt="Contact background"
            className="w-full h-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0f19]/95 via-[#0b0f19]/70 to-[#0b0f19]/40"></div>
        </div>
        <div className="relative z-10 max-w-[1560px] mx-auto px-6">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary mb-6 flex items-center gap-3">
            <span className="block w-8 h-[2px] bg-primary"></span>
            {t("contact.hero.kicker")}
          </div>
          <h1 className="text-white text-5xl md:text-7xl lg:text-[90px] font-black mb-6 leading-[1.05] tracking-tight">
            {t("contact.hero.title")}<span className="text-primary">.</span>
          </h1>
          <p className="text-gray-400 text-xl md:text-2xl font-light max-w-2xl leading-relaxed">
            {t("contact.hero.subtitle")}
          </p>
        </div>
      </div>

      {/* Info Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1560px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-gray-100">
            {INFO_ITEMS.map((item, i) => (
              <div key={i} className="flex items-start gap-5 py-10 lg:px-8">
                <div className="w-12 h-12 bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="font-semibold text-gray-900 hover:text-primary transition-colors text-base">
                      {item.value}
                    </a>
                  ) : (
                    <p className="font-semibold text-gray-900 text-base">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Form + Map */}
      <div className="bg-gray-50 py-24 lg:py-32">
        <div className="max-w-[1560px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

            {/* Left: Form */}
            <div className="lg:col-span-7">
              <div className="mb-12">
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{t("contact.form.title")}</h2>
                <p className="text-gray-500 text-lg font-light">{t("contact.form.subtitle")}</p>
              </div>

              {submitted ? (
                <div className="bg-white border border-gray-100 p-16 text-center">
                  <div className="w-20 h-20 bg-green-50 flex items-center justify-center mx-auto mb-6">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-10 h-10 text-green-500">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{t("contact.form.receivedTitle")}</h3>
                  <p className="text-gray-500 text-lg">{t("contact.form.receivedText")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white border border-gray-100 p-10 md:p-14 space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">{t("contact.form.firstName")}</label>
                      <input
                        type="text" name="firstname" value={form.firstname} onChange={handleChange} required
                        placeholder={t("contact.form.firstNamePlaceholder")}
                        className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-lg font-medium bg-transparent transition-colors placeholder-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">{t("contact.form.lastName")}</label>
                      <input
                        type="text" name="lastname" value={form.lastname} onChange={handleChange} required
                        placeholder={t("contact.form.lastNamePlaceholder")}
                        className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-lg font-medium bg-transparent transition-colors placeholder-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">{t("contact.form.email")}</label>
                      <input
                        type="email" name="email" value={form.email} onChange={handleChange} required
                        placeholder={t("contact.form.emailPlaceholder")}
                        className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-lg font-medium bg-transparent transition-colors placeholder-gray-300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">{t("contact.form.phone")}</label>
                      <input
                        type="tel" name="phone" value={form.phone} onChange={handleChange}
                        placeholder={t("contact.form.phonePlaceholder")}
                        className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-lg font-medium bg-transparent transition-colors placeholder-gray-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">{t("contact.form.subject")}</label>
                    <select
                      name="subject" value={form.subject} onChange={handleChange} required
                      className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-lg font-medium bg-transparent transition-colors"
                    >
                      <option value="">{t("contact.form.selectSubject")}</option>
                      <option value="enroll">{t("contact.form.enrollmentEnquiry")}</option>
                      <option value="scholarship">{t("contact.form.scholarshipApplication")}</option>
                      <option value="consultancy">{t("contact.form.consultancyServices")}</option>
                      <option value="sponsor">{t("contact.form.sponsorshipPartnership")}</option>
                      <option value="other">{t("contact.form.other")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">{t("contact.form.message")}</label>
                    <textarea
                      name="message" value={form.message} onChange={handleChange} required rows={5}
                      placeholder={t("contact.form.messagePlaceholder")}
                      className="w-full border-b border-gray-300 focus:border-primary pb-3 outline-none text-gray-900 text-lg font-medium bg-transparent transition-colors resize-none placeholder-gray-300"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-primary hover:bg-[#b0451e] text-white font-bold text-lg py-6 transition-colors duration-300 flex items-center justify-center gap-3 group"
                  >
                    {t("contact.form.send")}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5 group-hover:translate-x-1 transition-transform">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </form>
              )}
            </div>

            {/* Right: Side panel */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              {/* FAQ teaser */}
              <div className="bg-[#0b0f19] p-10">
                <h3 className="text-white text-2xl font-bold mb-6">{t("contact.faq.title")}</h3>
                <div className="space-y-6">
                  {t("contact.faq.items", { returnObjects: true }).map((faq, i) => (
                    <div key={i} className="border-b border-white/10 pb-6 last:border-0 last:pb-0">
                      <p className="text-white font-bold text-base mb-2">{faq.q}</p>
                      <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* WhatsApp CTA */}
              <a
                href="http://wa.me/12029710392"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-5 bg-[#25D366] p-8 hover:bg-[#1da951] transition-colors group"
              >
                <div className="w-14 h-14 bg-white/20 flex items-center justify-center shrink-0 group-hover:bg-white/30 transition-colors">
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-7 h-7 text-white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-black text-xl">{t("contact.whatsapp.title")}</p>
                  <p className="text-white/80 text-sm font-medium mt-1">{t("contact.whatsapp.subtitle")}</p>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5 text-white ml-auto group-hover:translate-x-1 transition-transform">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </a>

              {/* Map embed */}
              <div className="w-full h-64 overflow-hidden">
                <iframe
                  title="E1X Academy Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3104.437!2d-76.8602!3d38.9543!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z7404+Executive+Place+Lanham+MD!5e0!3m2!1sen!2sus!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

          </div>
        </div>
      </div>
    </MainLayouts>
  );
}

export default ContactPage;
